/**
 * Whether the annotation layer is on, and which mark is open.
 *
 * The prototype puts a numbered purple mark next to anything undecided and opens a
 * panel explaining what is undecided and which question decides it. That is the single
 * most useful thing in the prototype — and the single most dangerous thing to leave on
 * by default, because a screenshot with 39 purple markers in it is a screenshot of a
 * document, not of a product.
 *
 * So the layer is off unless two things are true: the dev overlay is enabled at all,
 * and the annotation toggle inside it is on. `active` is that conjunction, and
 * `orange-anno-marks` renders nothing whatsoever when it is false — no wrapper, no
 * whitespace, no aria node. The product screens are unchanged by its presence.
 *
 * `?anno=1` exists alongside the toggle for the same reason `?dev=1` does: a link that
 * lands someone on S-15 with the marks already up is worth more in review than a
 * sentence telling them which chord to press. It implies `?dev=1` is needed too — the
 * layer honours `dev.enabled()`, and a link that wants both says `?dev=1&anno=1`.
 *
 * ### Which screen, and why it is held here rather than passed as an input
 *
 * Annotations key on `ScreenId`. The shell knows the screen from the route's `PageMeta`,
 * which covers fourteen of the eighteen, and it reports it here on every navigation. The
 * four order screens share `/migrare/comanda/:reference` and deliberately carry no
 * `screen` in their meta, because which of S-10, S-14, S-15, S-16 or S-17 you are looking
 * at is a property of the order's status and not of the route. Those components set
 * {@link setScreenOverride} from the status they resolved, and clear it on destroy.
 *
 * Both live on the service rather than flowing down as component inputs because there are
 * two readers in different trees: the marks inside the router outlet, and the overlay's
 * *Adnotări* tab, which is a sibling of the whole shell and has no route of its own to
 * ask. One resolved answer, two subscribers.
 */
import { Injectable, computed, inject, signal } from '@angular/core';
import type { ScreenId } from '../flow/flow-steps';
import { DevMode } from './dev-mode';

const STORAGE_KEY = 'pre2post.poc.anno.v1';

@Injectable({ providedIn: 'root' })
export class Annotations {
  private readonly dev = inject(DevMode);

  private readonly onSignal = signal<boolean>(initialOn());
  private readonly routeSignal = signal<ScreenId | null>(null);
  private readonly overrideSignal = signal<ScreenId | null>(null);
  private readonly openSignal = signal<number | null>(null);

  /** The toggle's own state, independent of whether the overlay is enabled. */
  readonly on = this.onSignal.asReadonly();

  /**
   * What `orange-anno-marks` asks. Both conditions, so turning the overlay off takes
   * the marks with it and nobody has to remember two switches.
   */
  readonly active = computed(() => this.dev.enabled() && this.onSignal());

  /** Set by the order screens, which know their screen id only after the order loads. */
  readonly screenOverride = this.overrideSignal.asReadonly();

  /**
   * The screen both readers use. An override wins, because the component that resolved an
   * order's status knows more than the route it shares with four others.
   */
  readonly screen = computed<ScreenId | null>(() => this.overrideSignal() ?? this.routeSignal());

  /** Which mark's panel is open, as an index into the screen's own list. */
  readonly openIndex = this.openSignal.asReadonly();

  set(on: boolean): void {
    this.onSignal.set(on);
    if (!on) {
      this.openSignal.set(null);
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      // Same as DevMode: applies to this page load, will not survive a navigation.
    }
  }

  toggle(): void {
    this.set(!this.onSignal());
  }

  /**
   * The shell's hook, called on every navigation with whatever the route's meta declares.
   * Closes any open mark: the panel describes the screen you left.
   */
  setRouteScreen(screen: ScreenId | null): void {
    if (screen !== this.routeSignal()) {
      this.routeSignal.set(screen);
      this.openSignal.set(null);
    }
  }

  /** The order screens' hook. `null` hands the screen back to the route's meta. */
  setScreenOverride(screen: ScreenId | null): void {
    if (screen !== this.overrideSignal()) {
      this.overrideSignal.set(screen);
      this.openSignal.set(null);
    }
  }

  /** A mark was pressed. Pressing the open one closes it, as the prototype does. */
  toggleOpen(index: number): void {
    this.openSignal.update((current) => (current === index ? null : index));
  }

  close(): void {
    this.openSignal.set(null);
  }
}

function initialOn(): boolean {
  try {
    const fromUrl = new URLSearchParams(location.search).get('anno');
    if (fromUrl === '1' || fromUrl === '0') {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl === '1';
    }
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
